<script setup>
import { ref, computed, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);
const accountId = computed(() => getters.getCurrentAccountId.value);
const currentContact = computed(() => {
  const senderId = currentChat.value?.meta?.sender?.id;
  return senderId ? store.getters['contacts/getContact'](senderId) : null;
});

const hideNextMessage = async () => {
  closeDropdown();
  const chat = currentChat.value;
  const sender = chat?.meta?.sender || {};
  const contact = currentContact.value || {};
  const email = String(sender.email || contact.email || '').trim().toLowerCase();
  const phone = String(
    sender.phone_number || contact.phone_number || contact.phone || ''
  ).replace(/\D/g, '');
  const matchField = email ? 'sender_email' : phone ? 'sender_phone' : null;
  const matchValue = email || phone;

  if (!matchField) {
    useAlert('No sender email or phone number is available for this conversation.');
    return;
  }

  isLoading.value = true;
  try {
    const rulesUrl = `/api/v1/accounts/${accountId.value}/ai_message_rules`;
    const existing = (await window.axios.get(rulesUrl)).data.find(
      rule =>
        rule.active &&
        rule.match_field === matchField &&
        String(rule.match_value).toLowerCase() === matchValue
    );

    if (!existing) {
      await window.axios.post(rulesUrl, {
        ai_message_rule: {
          name: `Hide future messages from ${matchValue}`,
          rule_type: 'custom',
          match_field: matchField,
          match_operator: 'equals',
          match_value: matchValue,
          priority: 300,
          active: true,
          actions: { resolve: true, label: 'blocked-address' },
        },
      });
    }

    useAlert(
      existing
        ? 'This sender is already hidden by an active rule.'
        : 'Future messages from this sender will be resolved automatically.'
    );
    isLoading.value = false;
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  } catch (error) {
    isLoading.value = false;
    useAlert(error?.response?.data?.error || 'Could not create the hide rule.');
  }
};

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const openTopConversationAfterResolve = async (activeIndex, resolvedRow) => {
  // Wait for Vue/Vuex to remove the resolved item before selecting its replacement.
  // Clicking during that reconciliation briefly clears the conversation route and
  // produces the white flash/blank pane reported in the UI.
  for (let attempt = 0; attempt < 60 && resolvedRow?.isConnected; attempt += 1) {
    await new Promise(resolve => requestAnimationFrame(resolve));
  }
  await nextTick();

  const rows = [...document.querySelectorAll('.conversations-list .conversation')];
  // The resolved row is removed by the store; the following row shifts into
  // the same index. Selecting it directly preserves scroll position and the
  // current list view without a route reload or white flash.
  const nextConversation = rows[activeIndex] || rows[0];
  if (nextConversation) {
    nextConversation.click();
    return;
  }

  // No conversations remain: leave the list and conversation pane blank.
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;
  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  await router.push(
    conversationListPageURL({
      accountId: store.getters.getCurrentAccountId,
      inboxId,
      label,
      teamId,
      conversationType: conversationTypeMap[name],
      customViewId,
    })
  );
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const rowsBeforeResolve = [
    ...document.querySelectorAll('.conversations-list .conversation'),
  ];
  const activeIndex = Math.max(0, rowsBeforeResolve.indexOf(activeConversation));
  const resolvedRow = activeConversation;

  store.dispatch('toggleStatus', payload).then(async () => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
    if (status === wootConstants.STATUS_TYPE.RESOLVED) {
      await openTopConversationAfterResolve(activeIndex, resolvedRow);
    }
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center resolve-actions">
    <Button
      v-if="isOpen"
      label="Hide next message"
      size="sm"
      color="slate"
      no-animation
      :is-loading="isLoading"
      @click="hideNextMessage"
    />
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
